import {
  ProductClass,
} from '../../graphQL/types/shared';
import {
  ICPYDatum,
  ICYDatum,
  ILoadable,
  IPYDatum,
  ProductMetadatumLevel as Level,
} from '../../Utils';
import {
  getIntervalWidener,
} from '../../viz/Utils';
import getSize from '../../workerStore/network/getSize';
import {
  IMetadatum,
} from '../fetchedData/productMetadata';
import getAggregate from './aggregate';
import applyCategoryFilter from './applyCategoryFilter';
import assignGroup from './assignGroup';
import getAssignRadius from './assignRadius';
import assignSection from './assignSection';
import getAssignY from './assignY';
import filterByYear from './filterByYear';
import {
  IHasNoError,
  IInputFromURLRouting,
  ISuccessfulMergeAction,
} from './getReducer';
import getValueGetter from './getValueFunction';
import {
  updateMergedData,
} from './reducer';

import {
  maxCircleRadius,
  minCircleRadius,
  xDomainWideningFactor,
  yDomainWideningFactor,
} from './Utils';
import {
  HideExports,
  YAxisMeasure,
} from './Utils';

export interface IComputationInput {
  svgWidth: number | undefined;
  svgHeight: number | undefined;
  high2Low: ILoadable<Record<string, number>>;
  high2Mid: ILoadable<Record<string, number>>;
  dataCPY_C: ILoadable<ICPYDatum[]>;
  dataPY: ILoadable<IPYDatum[]>;
  availableYears: ILoadable<number[]>;
  metadata: ILoadable<Record<string, IMetadatum>>;
  dataCY: ILoadable<ICYDatum[]>;
  hideExports: HideExports;
  detailLevel: Level;
  productClass: ProductClass;
  year: number;
  yAxisMeasure: YAxisMeasure;
  deselectedCategories: number[];
}

const widenIntervalX = getIntervalWidener(xDomainWideningFactor);
const widenIntervalY = getIntervalWidener(yDomainWideningFactor);
const aggregate = getAggregate(widenIntervalX);
const assignY = getAssignY(widenIntervalY);
const assignRadius = getAssignRadius(minCircleRadius, maxCircleRadius);

interface IComputeInput {
  // Info that's actually used in the computation:
  computationInput: IComputationInput;

  // Pass through to hash function:
  queueNumber: number;
  inputFromURLRouting: IInputFromURLRouting;
}

const compute = (
    {computationInput, queueNumber, inputFromURLRouting}: IComputeInput,
  ): ISuccessfulMergeAction => {

  const {
    svgWidth, svgHeight, detailLevel, productClass,
    high2Low, high2Mid, dataPY, availableYears,
    metadata, hideExports, dataCPY_C, year, yAxisMeasure,
    deselectedCategories, dataCY,
  } = computationInput;
  const {
    country, queryLevel
  } = inputFromURLRouting;
  const size = getSize(svgWidth, svgHeight);
  const getValue = getValueGetter(hideExports);
  const sectionAssigned = assignSection(hideExports, productClass, dataCPY_C, dataPY, high2Low);
  const groupAssigned = assignGroup(detailLevel, high2Low, high2Mid, sectionAssigned, productClass);
  const filtered = filterByYear(year, groupAssigned, dataCY, productClass);
  const aggregated = aggregate(getValue, detailLevel, metadata, filtered, size, year, productClass);
  const assignedY = assignY(yAxisMeasure, aggregated, size);
  const assignedRadius = assignRadius(getValue, assignedY);
  const value = applyCategoryFilter(
    hideExports, getValue, deselectedCategories, availableYears, assignedRadius, size, productClass,
  );
  const mergedData: IHasNoError = {
    hasError: false, value,
  };
  const computationalResult = {
    mergedData,
    hashInput: {
      detailLevel, yAxisMeasure, hideExports,
      deselectedCategories,
      svgHeight, svgWidth,
      country, queryLevel, productClass, year,
    },
    queueNumber,
  };
  return updateMergedData(computationalResult);
};

export default compute;
